import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createBooking } from '../../../api/bookingApi';
import { SubHeading } from '../../../components/ClientPage';
import { toISO } from '../../../utils/dateUtils';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../css/BookingForm.css';
import { useNavigate } from 'react-router-dom';

const BookingForm = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    customer: {
      customerName: '',
      phoneNumber: '',
      email: ''
    },
    numGuests: 1,
    tableIds: [],
    bookingAt: '',
    note: ''
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [bookingResponse, setBookingResponse] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);

  // Separate state for first and last name inputs
  const [nameInputs, setNameInputs] = useState({
    firstName: '',
    lastName: ''
  });

  // Separate state for date and time - using Date object for DatePicker
  const [dateTimeInputs, setDateTimeInputs] = useState({
    date: null, // Changed to null for DatePicker
    time: ''
  });

  const handleNameChange = (field, value) => {
    const newNameInputs = {
      ...nameInputs,
      [field]: value
    };
    setNameInputs(newNameInputs);

    // Update customerName in formData
    const fullName = `${newNameInputs.firstName} ${newNameInputs.lastName}`.trim();
    setFormData(prev => ({
      ...prev,
      customer: {
        ...prev.customer,
        customerName: fullName
      }
    }));

    // Clear validation errors when user starts editing
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };

  // Generate time slots in 30-minute intervals
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 11; hour < 22; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push(time);
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  const handleDateTimeChange = (field, value) => {
    const newDateTimeInputs = {
      ...dateTimeInputs,
      [field]: value,
    };
    setDateTimeInputs(newDateTimeInputs);

    let bookingAt = '';

    const { date, time } = newDateTimeInputs;

    if (date && time) {
      bookingAt = toISO(date, time);
    }

    setFormData((prev) => ({
      ...prev,
      bookingAt,
    }));
  };

  const handleGuestCountChange = (value) => {
    setFormData(prev => ({
      ...prev,
      numGuests: value
    }));
    
    // Clear validation errors when user starts editing
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    // Clear validation errors when user starts editing
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const bookingData = {
        customer: {
          customerName: formData.customer.customerName,
          phoneNumber: formData.customer.phoneNumber,
          email: formData.customer.email
        },
        numGuests: parseInt(formData.numGuests),
        tableIds: formData.tableIds,
        bookingAt: formData.bookingAt,
        note: formData.note
      };
      const response = await createBooking(bookingData);
      setBookingResponse(response);
      setSuccess(true);
      
      // Reset form
      setFormData({
        customer: {
          customerName: '',
          phoneNumber: '',
          email: ''
        },
        numGuests: 1,
        tableIds: [],
        bookingAt: '',
        note: ''
      });
      setNameInputs({
        firstName: '',
        lastName: ''
      });
      setDateTimeInputs({
        date: null, // Reset to null for DatePicker
        time: ''
      });
    } catch (err) {
      console.error('Error creating booking:', err);
      setError(t('booking.messages.failedToCreateBooking'));
    } finally {
      setLoading(false);
    }
  };

  if (success && bookingResponse) {
    return (
      <div className="app__booking app__bg section__padding" id="booking">
        <div className="app__booking-success">
          <SubHeading title={t('booking.bookingConfirmed')} />
          <h1 className="headtext__cormorant">{t('booking.thankYou')}</h1>
          <div className="app__booking-success-details">
            <div className="booking-detail">
              <h3 className="p__cormorant">{t('booking.bookingId')}: {bookingResponse.bookingId}</h3>
              <p className="p__opensans">{t('booking.customer')}: {bookingResponse.customer?.customerName}</p>
              <p className="p__opensans">{t('booking.phone')}: {bookingResponse.customer?.phoneNumber}</p>
              <p className="p__opensans">{t('booking.email')}: {bookingResponse.customer?.email}</p>
              <p className="p__opensans">{t('booking.guests')}: {bookingResponse.numGuests}</p>
              <p className="p__opensans">{t('booking.bookingTime')}: {bookingResponse.bookingAt.replace('T',' ')}</p>
              <p className="p__opensans">{t('booking.status')}: {bookingResponse.status}</p>
              <p className="p__opensans">{t('booking.note')}: {bookingResponse.note}</p>
              {bookingResponse.tables && bookingResponse.tables.length > 0 && (
                <div className="booked-tables">
                  <p className="p__opensans">{t('booking.tables')}</p>
                  {bookingResponse.tables.map((table, index) => (
                    <span key={index} className="table-tag">
                      {table.tableName} (Capacity: {table.capacity})
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="app_booking-button-redirect">
          <button 
            type="button" 
            className="custom__button"
            onClick={() => setSuccess(false)}
          >
            {t('booking.makeAnotherBooking')}
          </button>
          <button
            type="button"
            className="custom__button"
            onClick={() => navigate('/profile')}
          >
            {t('booking.viewProfile')}
          </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app__booking app__bg section__padding" id="booking">
      <div className="app__booking-form_overlay flex__center">
        <div className="app__booking-form_container">
          <SubHeading title={t('booking.title')} />
          <h1 className="headtext__cormorant">{t('booking.subtitle')}</h1>
          
          {error && <div className="error-message">{error}</div>}
          
          <form className="app__booking-form" onSubmit={handleSubmit}>
            {/* Customer Information */}
            <div className="form-section">
              <h3 className="p__cormorant group-title">{t('booking.customerInfo')}</h3>
              <div className="form-row">
                <div className="form-group">
                  <h4 className="p__cormorant" style={{ marginBottom: '8px', fontSize: '18px' }}>{t('booking.firstName')} * :</h4>
                  <input
                    type="text"
                    value={nameInputs.firstName}
                    onChange={(e) => handleNameChange('firstName', e.target.value)}
                    className="client-booking-form-input"
                    placeholder={t('booking.placeholders.firstName')}
                    required
                  />
                </div>
                <div className="form-group">
                  <h4 className="p__cormorant" style={{ marginBottom: '8px', fontSize: '18px' }}>{t('booking.lastName')} * :</h4>
                  <input
                    type="text"
                    value={nameInputs.lastName}
                    onChange={(e) => handleNameChange('lastName', e.target.value)}
                    className="client-booking-form-input"
                    placeholder={t('booking.placeholders.lastName')}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <h4 className="p__cormorant" style={{ marginBottom: '8px', fontSize: '18px' }}>{t('booking.phoneNumber')} * :</h4>
                  <input
                    type="tel"
                    placeholder={t('booking.placeholders.phoneNumber')}
                    value={formData.customer.phoneNumber}
                    onChange={(e) => handleInputChange('customer.phoneNumber', e.target.value)}
                    required
                    className="client-booking-form-input"
                  />
                </div>
                <div className="form-group">
                  <h4 className="p__cormorant" style={{ marginBottom: '8px', fontSize: '18px' }}>{t('booking.email')}</h4>
                  <input
                    type="email"
                    placeholder={t('booking.placeholders.email')}
                    value={formData.customer.email}
                    onChange={(e) => handleInputChange('customer.email', e.target.value)}
                    className="client-booking-form-input"
                  />
                </div>
              </div>
            </div>

            {/* Booking Information */}
            <div className="form-section">
              <h3 className="p__cormorant group-title">{t('booking.bookingInfo')}</h3>
              <div className="form-row">
                <div className="form-group">
                  <h4 className="p__cormorant" style={{ marginBottom: '8px', fontSize: '18px' }}>{t('booking.numGuests')} * :</h4>
                  <input
                    type="number"
                    min="1"
                    placeholder={t('booking.placeholders.numGuests')}
                    value={formData.numGuests}
                    onChange={(e) => handleGuestCountChange(e.target.value)}
                    required
                    className="client-booking-form-input"
                  />
                </div>
                <div className="form-group">
                  <h4 className="p__cormorant" style={{ marginBottom: '8px', fontSize: '18px' }}>{t('booking.bookingDate')} * :</h4>
                  <DatePicker
                    selected={dateTimeInputs.date}
                    onChange={(date) => handleDateTimeChange('date', date)}
                    minDate={new Date()}
                    dateFormat="yyyy-MM-dd"
                    placeholderText={t('booking.placeholders.selectDate') || 'Select date'}
                    className="client-booking-form-input date-picker-input"
                    required
                    showPopperArrow={false}
                    popperPlacement="bottom-start"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <h4 className="p__cormorant" style={{ marginBottom: '8px', fontSize: '18px' }}>{t('booking.bookingTime')} * :</h4>
                  <select
                    value={dateTimeInputs.time}
                    onChange={(e) => handleDateTimeChange('time', e.target.value)}
                    required
                    className="client-booking-form-input"
                  >
                    <option value="">{t('booking.placeholders.selectTime')}</option>
                    {timeSlots.map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>
              </div>
              {/* <div
                className="payment-note"
                style={{
                  backgroundColor: 'rgba(255, 230, 150, 0.2)',
                  padding: '10px 12px',
                  borderRadius: '6px',
                  fontSize: '20px',
                  textAlign: 'center',
                  color: 'var(--color-golden)',
                }}
              >
                {t('booking.paymentNote')}
              </div> */}
            </div>

            {/* Note */}
            <div className="form-section">
              <h3 className="p__cormorant group-title">{t('booking.note')}</h3>
              <div className="form-group">
                <textarea
                  name="note"
                  placeholder={t('booking.placeholders.note')}
                  value={formData.note}
                  onChange={(e) => handleInputChange('note', e.target.value)}
                  className="form-textarea"
                  rows="4"
                />
              </div>
            </div>

            <button 
              type="submit" 
              className="custom__button"
              disabled={loading}
            >
              {loading ? t('booking.booking') : t('booking.bookTable')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BookingForm;
